import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { importPreparedProducts } from 'api/apiProducts';
import ImportCsvInfo from './ImportCsvInfo';
import ImportCsvTable from './ImportCsvTable';
import ImportIaInfo from './ImportIaInfo';
import type { ImportProductItem } from './model/types';

export type ProductImportMode = 'csv' | 'ia';

interface OffImportProps {
    show: boolean;
    setShow: (show: boolean) => void;
    mode: ProductImportMode | null;
    onImported?: () => void;
}

const OffImport = ({ show, setShow, mode, onImported }: OffImportProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const { settings, localSettings } = useSettings();
    const params = useSettingsParams();
    const { showToast } = useToast();
    const [importItems, setImportItems] = useState<ImportProductItem[]>([]);
    const [pricesInUsd, setPricesInUsd] = useState(() => Boolean(params.usdDefault));
    const [isImporting, setIsImporting] = useState(false);

    const vendorsForParse = useMemo(
        () =>
            (settings?.DIRS?.Vendor || [])
                .filter((vendor) => vendor.ID != null)
                .map((vendor) => ({
                    ID: vendor.ID as number,
                    vendorName: vendor.vendorName || '',
                })),
        [settings],
    );

    const vendorsForSelect = useMemo(
        () =>
            (settings?.DIRS?.Vendor || [])
                .filter((vendor) => vendor.ID != null && !vendor.hidden)
                .map((vendor) => ({
                    ID: vendor.ID as number,
                    vendorName: vendor.vendorName || '',
                })),
        [settings],
    );

    useEffect(() => {
        if (!show) {
            setImportItems([]);
            setPricesInUsd(Boolean(params.usdDefault));
        }
    }, [show, params.usdDefault]);

    const title =
        mode === 'csv'
            ? t('importCsv.title')
            : mode === 'ia'
              ? t('importIa.title')
              : t('productAdmin.menuTitle');

    const handleClose = () => setShow(false);
    const usdRate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = usdRate === 1;
    const normalizePriceToUsd = (price: number | null): number | null => {
        if (price === null) return null;
        if (isUahMode || pricesInUsd) return price;
        if (!Number.isFinite(usdRate) || usdRate <= 0) return price;
        return Number((price / usdRate).toFixed(2));
    };

    const appendImportItems = (nextItems: ImportProductItem[]) => {
        if (!nextItems.length) return;
        setImportItems((prev) => [
            ...prev,
            ...nextItems.map((item, index) => ({
                ...item,
                prodDefPrice: normalizePriceToUsd(item.prodDefPrice),
                line: prev.length + index + 1,
            })),
        ]);
    };

    const handleClearItems = () => setImportItems([]);

    const handleImport = async () => {
        if (!importItems.length || isImporting) return;
        setIsImporting(true);
        try {
            const result = await importPreparedProducts(
                importItems.map((item) => ({
                    prodArticle: item.prodArticle,
                    prodName: item.prodName,
                    prodDefPrice: item.prodDefPrice,
                    vendorId: item.vendorId,
                })),
            );

            if (!result?.stat) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            showToast(
                t('importCsv.messImported', {
                    imported: result.imported,
                    adjusted: result.adjusted,
                    skipped: result.skipped,
                }),
                'success',
            );
            setImportItems([]);
            onImported?.();
            setShow(false);
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="off-import">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {(mode === 'csv' || mode === 'ia') && (
                    <>
                        {mode === 'csv' && (
                            <ImportCsvInfo
                                vendors={vendorsForParse}
                                itemsCount={importItems.length}
                                onParsed={appendImportItems}
                                pricesInUsd={pricesInUsd}
                                onPricesInUsdChange={setPricesInUsd}
                                usdRate={usdRate}
                                isImporting={isImporting}
                                onImport={handleImport}
                            />
                        )}
                        {mode === 'ia' && (
                            <ImportIaInfo
                                vendors={vendorsForParse}
                                itemsCount={importItems.length}
                                onParsed={appendImportItems}
                                pricesInUsd={pricesInUsd}
                                onPricesInUsdChange={setPricesInUsd}
                                usdRate={usdRate}
                                isImporting={isImporting}
                                onImport={handleImport}
                            />
                        )}
                    </>
                )}

                {(mode === 'csv' || mode === 'ia') && importItems.length > 0 ? (
                    <ImportCsvTable
                        items={importItems}
                        vendors={vendorsForSelect}
                        allVendors={vendorsForParse}
                        onItemsChange={setImportItems}
                        onClear={handleClearItems}
                    />
                ) : (
                    <p className="text-muted small mt-3 mb-0">{t('importPlaceholder')}</p>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffImport;
